import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { conteoFisicoService, ConteoFisicoCreate } from '../../services/conteoFisicoService';
import { sucursalService } from '../../services/sucursalService';
import { useAuthStore } from '../../store/authStore';

interface ConteoFisicoFormProps {
  onClose: () => void;
}

const ConteoFisicoForm = ({ onClose }: ConteoFisicoFormProps) => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const { data: sucursalesData } = useQuery(
    'sucursales',
    () => sucursalService.getAll(1, 100)
  );

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<ConteoFisicoCreate>({
    defaultValues: {
      sucursalId: 0,
      notas: ''
    }
  });

  const createMutation = useMutation(conteoFisicoService.create, {
    onSuccess: () => {
      toast.success('Conteo físico creado correctamente');
      queryClient.invalidateQueries(['conteos-fisicos']);
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al crear conteo físico');
    }
  });

  const onSubmit = (data: ConteoFisicoCreate) => {
    createMutation.mutate(data);
  };

  const sucursales = sucursalesData?.data.sucursales || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">Nuevo Conteo Físico</h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sucursal *
            </label>
            <select
              {...register('sucursalId', {
                required: 'Sucursal es requerida',
                valueAsNumber: true,
                min: { value: 1, message: 'Selecciona una sucursal' }
              })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value={0}>Selecciona una sucursal</option>
              {sucursales.map((sucursal) => (
                <option key={sucursal.id} value={sucursal.id}>
                  {sucursal.name}
                </option>
              ))}
            </select>
            {errors.sucursalId && (
              <p className="text-red-500 text-xs mt-1">{errors.sucursalId.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas (opcional)
            </label>
            <textarea
              {...register('notas')}
              rows={3}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Notas adicionales sobre el conteo..."
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {createMutation.isLoading ? 'Creando...' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConteoFisicoForm;

